import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { MessageSquare, Clock, Zap, Loader2, History, RefreshCcw, Repeat, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import TopUpDialog from "../usage/TopUpDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";

const fmt = (n) => (n === null || n === undefined ? "∞" : Number(n).toLocaleString());
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

const barColor = (pct) => {
  if (pct === null) return "bg-emerald-500";
  if (pct >= 100) return "bg-red-500";
  if (pct >= 90) return "bg-orange-500";
  if (pct >= 75) return "bg-amber-500";
  return "bg-blue-500";
};

const UsageBar = ({ icon: Icon, iconColor, label, block, onBuy, testId }) => {
  const pct = block.unlimited ? null : Math.min(block.pct || 0, 100);
  return (
    <div className="rounded-xl border border-border bg-card/60 p-4 space-y-2" data-testid={testId}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${iconColor}`} />
          <span className="text-sm font-medium text-foreground">{label}</span>
          {pct !== null && pct >= 100 && <Badge className="bg-red-500/15 text-red-500 border-0 text-[10px]">Limit reached</Badge>}
        </div>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onBuy} data-testid={`${testId}-buy-btn`}>
          <Zap className="w-3 h-3 mr-1 text-[#FFCC00]" /> Buy More
        </Button>
      </div>
      <div className="text-2xl font-bold text-foreground tabular-nums">
        {fmt(block.used)}<span className="text-base font-normal text-muted-foreground"> / {block.unlimited ? "∞" : fmt(block.allowance)}</span>
      </div>
      <div className="h-2 rounded-full overflow-hidden bg-muted/60">
        <div className={`h-full ${barColor(block.unlimited ? null : block.pct)} transition-all`}
          style={{ width: `${block.unlimited ? 4 : Math.min(block.pct || 0, 100)}%` }} />
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        {!block.unlimited && <span>{(block.pct || 0).toFixed(1)}% used</span>}
        {!block.unlimited && <span className="text-foreground font-medium">{fmt(block.remaining)} remaining</span>}
        <span>{fmt(block.included)} included{block.topup > 0 && <> + <span className="text-emerald-500 font-medium">{fmt(block.topup)} top-up</span></>}</span>
      </div>
    </div>
  );
};

const ADDON_STATUS = {
  active: { label: "Active", cls: "bg-emerald-500/15 text-emerald-500" },
  pending_payment: { label: "Payment processing", cls: "bg-amber-500/15 text-amber-500" },
  past_due: { label: "Payment failed", cls: "bg-red-500/15 text-red-500" },
  canceled: { label: "Canceled", cls: "bg-muted text-muted-foreground" },
};

const UsageTopupsSection = ({ API, getAuthConfig, refetchLimits }) => {
  const [account, setAccount] = useState(null);
  const [topups, setTopups] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [packages, setPackages] = useState([]);
  const [autoTopup, setAutoTopup] = useState(null);
  const [addons, setAddons] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const [savingAuto, setSavingAuto] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyType, setBuyType] = useState(null);

  const load = useCallback(async () => {
    try {
      const [acct, tps, pds, pkgs, auto, adns] = await Promise.all([
        axios.get(`${API}/usage/account`, getAuthConfig()),
        axios.get(`${API}/usage/topups`, getAuthConfig()),
        axios.get(`${API}/usage/periods`, getAuthConfig()),
        axios.get(`${API}/usage/packages`, getAuthConfig()),
        axios.get(`${API}/usage/auto-topup`, getAuthConfig()),
        axios.get(`${API}/usage/addons`, getAuthConfig()).catch(() => ({ data: null })),
      ]);
      setAccount(acct.data);
      setTopups(tps.data.topups || []);
      setPeriods(pds.data.periods || []);
      setPackages(pkgs.data.packages || []);
      setAutoTopup(auto.data);
      setAddons(adns.data);
    } catch { /* section hides on failure */ }
  }, [API, getAuthConfig]);

  const confirmCancelAddon = async () => {
    if (!cancelTarget) return;
    setCanceling(true);
    try {
      const res = await axios.post(`${API}/usage/addons/${cancelTarget.addon_id}/cancel`, {}, getAuthConfig());
      toast.success(`Add-on canceled — your allowance stays until ${fmtDate(res.data.ends_at)}. No further charges.`);
      setCancelTarget(null);
      load();
      if (refetchLimits) refetchLimits();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel add-on");
    } finally {
      setCanceling(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle Stripe return: ?topup=success&session_id=cs_...
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("topup") !== "success") return;
    const sid = params.get("session_id");
    params.delete("topup"); params.delete("session_id");
    const clean = `${window.location.pathname}${params.toString() ? "?" + params.toString() : ""}`;
    window.history.replaceState({}, "", clean);
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${API}/usage/topup/status/${sid}`, getAuthConfig());
        if (res.data.payment_status === "paid") {
          const pkg = res.data.package;
          toast.success(`Top-up added: +${Number(pkg?.quantity || 0).toLocaleString()} ${pkg?.usage_type === "sms" ? "SMS" : "minutes"}`);
          load();
          if (refetchLimits) refetchLimits();
          return;
        }
        if (res.data.status === "expired") { toast.error("Top-up payment was not completed."); return; }
      } catch { /* retry */ }
      if (attempts < 6) setTimeout(poll, 2500);
      else toast.info("Payment is still processing — your top-up will be credited automatically once confirmed.");
    };
    poll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveAutoTopup = async (update) => {
    setSavingAuto(true);
    try {
      const res = await axios.put(`${API}/usage/auto-topup`, { ...autoTopup, ...update }, getAuthConfig());
      setAutoTopup(res.data);
      toast.success("Auto top-up settings saved");
    } catch { toast.error("Failed to save auto top-up settings"); }
    finally { setSavingAuto(false); }
  };

  if (!account) return null;

  const smsPkgs = packages.filter(p => p.usage_type === "sms");
  const minPkgs = packages.filter(p => p.usage_type === "minutes");
  const openBuy = (type) => { setBuyType(type); setBuyOpen(true); };

  return (
    <Card className="glass-card border-theme" data-testid="usage-topups-section">
      <CardHeader className="p-4 sm:p-6 pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle className="text-foreground font-['Manrope'] text-base sm:text-lg">Usage &amp; Top-Ups</CardTitle>
            <CardDescription className="text-xs sm:text-sm" data-testid="usage-billing-period">
              Billing period: {fmtDate(account.period.start)} – {fmtDate(account.period.end)} · Next reset: {fmtDate(account.period.next_reset)}
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={load} data-testid="usage-refresh-btn">
            <RefreshCcw className="w-3 h-3 mr-1" /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-4 sm:p-6 pt-2 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <UsageBar icon={MessageSquare} iconColor="text-purple-500" label="SMS Messages"
            block={account.sms} onBuy={() => openBuy("sms")} testId="usage-sms-block" />
          <UsageBar icon={Clock} iconColor="text-orange-500" label="Call Minutes"
            block={account.minutes} onBuy={() => openBuy("minutes")} testId="usage-minutes-block" />
        </div>

        {(account.sms.by_location?.length > 0 || account.minutes.by_location?.length > 0) && (
          <div className="rounded-xl border border-border bg-card/40 p-3" data-testid="usage-location-breakdown">
            <p className="text-xs font-medium text-muted-foreground mb-2">Usage by location</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
              {account.sms.by_location?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">SMS</p>
                  {account.sms.by_location.map((l, i) => (
                    <div key={i} className="flex justify-between text-xs py-0.5">
                      <span className="text-muted-foreground">{l.name}</span>
                      <span className="text-foreground tabular-nums">{fmt(l.used)}</span>
                    </div>
                  ))}
                </div>
              )}
              {account.minutes.by_location?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Minutes</p>
                  {account.minutes.by_location.map((l, i) => (
                    <div key={i} className="flex justify-between text-xs py-0.5">
                      <span className="text-muted-foreground">{l.name}</span>
                      <span className="text-foreground tabular-nums">{fmt(l.used)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Monthly recurring add-ons */}
        {addons && (addons.addons?.length > 0 || addons.recurring_available) && (
          <div className="rounded-xl border border-border bg-card/40 p-3 space-y-2" data-testid="active-addons-card">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-foreground flex items-center gap-1.5">
                <Repeat className="w-3.5 h-3.5 text-[#0046BE]" /> Monthly Add-ons
              </p>
              <span className="text-[11px] text-muted-foreground">
                {addons.recurring_available
                  ? `Renews with your plan on ${fmtDate(addons.period_end)} · 5% off vs one-time`
                  : "One-time packs available — yours until used up"}
              </span>
            </div>
            {(addons.addons || []).length === 0 ? (
              <p className="text-[11px] text-muted-foreground">
                No add-ons yet. Use "Buy More" above to add a monthly SMS or Minutes package — it bills together with your subscription.
              </p>
            ) : (
              <div className="space-y-1.5">
                {addons.addons.map(a => {
                  const st = ADDON_STATUS[a.status] || ADDON_STATUS.active;
                  return (
                    <div key={a.addon_id} className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-card/60 px-3 py-2"
                      data-testid={`addon-row-${a.addon_id}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-semibold text-foreground">{a.name}</span>
                          <Badge className={`${st.cls} border-0 text-[9px]`} data-testid={`addon-status-${a.addon_id}`}>{st.label}</Badge>
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          +{Number(a.quantity).toLocaleString()} {a.usage_type === "sms" ? "SMS" : "minutes"}/month · ${Number(a.monthly_price).toFixed(2)}/mo
                          {a.status === "canceled"
                            ? <> · <span className="text-amber-500">ends {fmtDate(a.ends_at)}</span></>
                            : a.status === "past_due"
                              ? <> · <span className="text-red-500">renewal not applied — update your payment method</span></>
                              : <> · renews {fmtDate(a.next_renewal)}</>}
                        </p>
                      </div>
                      {["active", "past_due", "pending_payment"].includes(a.status) && (
                        <Button size="sm" variant="outline" className="h-7 text-xs text-red-500 border-red-500/40 hover:bg-red-500/10 shrink-0"
                          onClick={() => setCancelTarget(a)} data-testid={`addon-cancel-${a.addon_id}`}>
                          Cancel
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Auto top-up */}
        {autoTopup && (
          <div className="rounded-xl border border-border bg-card/40 p-3 space-y-3" data-testid="auto-topup-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Automatic Top-Up</p>
                <p className="text-[11px] text-muted-foreground">Automatically purchase a package when your remaining allowance drops below the trigger. Charged to your saved payment method.</p>
              </div>
              <Switch checked={!!autoTopup.enabled} disabled={savingAuto}
                onCheckedChange={(v) => saveAutoTopup({ enabled: v })}
                data-testid="auto-topup-switch" />
            </div>
            {autoTopup.enabled && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <p className="text-[11px] text-muted-foreground">SMS package (buys when &lt; {autoTopup.sms_trigger_remaining} left)</p>
                  <Select value={autoTopup.sms_package_id || "none"}
                    onValueChange={(v) => saveAutoTopup({ sms_package_id: v === "none" ? null : v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid="auto-topup-sms-pkg"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Disabled</SelectItem>
                      {smsPkgs.map(p => <SelectItem key={p.package_id} value={p.package_id}>{p.name} — ${p.price}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <p className="text-[11px] text-muted-foreground">Minutes package (buys when &lt; {autoTopup.minutes_trigger_remaining} left)</p>
                  <Select value={autoTopup.minutes_package_id || "none"}
                    onValueChange={(v) => saveAutoTopup({ minutes_package_id: v === "none" ? null : v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid="auto-topup-minutes-pkg"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Disabled</SelectItem>
                      {minPkgs.map(p => <SelectItem key={p.package_id} value={p.package_id}>{p.name} — ${p.price}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <p className="text-[11px] text-muted-foreground">Monthly spend cap ($)</p>
                  <Input type="number" className="h-8 text-xs w-32" defaultValue={autoTopup.monthly_cap}
                    onBlur={(e) => saveAutoTopup({ monthly_cap: parseFloat(e.target.value) || 0 })}
                    data-testid="auto-topup-cap-input" />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Top-up history */}
        {topups.length > 0 && (
          <div className="rounded-xl border border-border bg-card/40 p-3" data-testid="topup-history">
            <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1"><History className="w-3 h-3" /> Top-Up History</p>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {topups.map(t => (
                <div key={t.topup_id} className="flex items-center justify-between text-xs py-1 border-b border-border/50 last:border-0">
                  <div>
                    <span className="text-foreground font-medium">{t.name}</span>
                    <span className="text-muted-foreground ml-2">{fmtDate(t.purchased_at)}</span>
                    {t.source === "auto" && <Badge className="ml-2 bg-blue-500/15 text-blue-500 border-0 text-[9px]">Auto</Badge>}
                    {t.source === "admin_grant" && <Badge className="ml-2 bg-purple-500/15 text-purple-500 border-0 text-[9px]">Granted</Badge>}
                    {t.status === "refunded" && <Badge className="ml-2 bg-red-500/15 text-red-500 border-0 text-[9px]">Refunded</Badge>}
                    {t.status === "expired" && <Badge className="ml-2 bg-muted text-muted-foreground border-0 text-[9px]">Expired</Badge>}
                    {t.status === "active" && t.expiration !== "period_end" && <Badge className="ml-2 bg-emerald-500/15 text-emerald-500 border-0 text-[9px]">Until used up</Badge>}
                  </div>
                  <span className="text-muted-foreground tabular-nums">
                    {fmt(t.remaining)} / {fmt(t.quantity)} left {t.price > 0 && `· $${Number(t.price).toFixed(2)}`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Historical periods */}
        {periods.length > 1 && (
          <div className="rounded-xl border border-border bg-card/40 p-3" data-testid="usage-period-history">
            <p className="text-xs font-medium text-muted-foreground mb-2">Previous Billing Periods</p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {periods.filter(p => p.period_key !== account.period.key).map(p => (
                <div key={p.period_key} className="flex items-center justify-between text-xs py-1 border-b border-border/50 last:border-0">
                  <span className="text-muted-foreground">{fmtDate(p.period_start)} – {fmtDate(p.period_end)}</span>
                  <span className="text-foreground tabular-nums">
                    SMS {fmt(Math.round(p.sms_used || 0))} · Min {fmt(Math.round(p.minutes_used || 0))}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
      <TopUpDialog open={buyOpen} onOpenChange={setBuyOpen} usageType={buyType} onPurchased={() => { load(); if (refetchLimits) refetchLimits(); }} />

      <AlertDialog open={!!cancelTarget} onOpenChange={(v) => !v && setCancelTarget(null)}>
        <AlertDialogContent data-testid="addon-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" /> Cancel this add-on?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {cancelTarget && (
                <>You'll keep the <b>+{Number(cancelTarget.quantity).toLocaleString()} {cancelTarget.usage_type === "sms" ? "SMS" : "minutes"}</b> you
                already paid for until <b>{fmtDate(addons?.period_end)}</b>. After that, the add-on ends and you won't be charged again.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="addon-cancel-keep">Keep Add-on</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancelAddon} disabled={canceling}
              className="bg-red-600 hover:bg-red-700 text-white" data-testid="addon-cancel-confirm">
              {canceling ? "Canceling…" : "Cancel Add-on"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default UsageTopupsSection;
